import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { FileText, Shield, Users, Download, Send, Loader2, Menu, MessageSquare, Copy, Check, RefreshCw, Link2, Eye } from 'lucide-react';
import { useState, useEffect } from 'react';
import type { Therapist, TranslateFn } from '../../types';

const API_URL = process.env.REACT_APP_BACKEND_URL;

interface ManualsTabProps {
  t: TranslateFn;
  therapists: Therapist[];
  sendingManual: Record<string, boolean>;
  handleSendManualViaLine: (...args: any[]) => void;
  token: string;
}

export const ManualsTab = ({ t, therapists, sendingManual, handleSendManualViaLine, token }: ManualsTabProps) => {
  const [settingUpMenu, setSettingUpMenu] = useState(false);
  const [menuResult, setMenuResult] = useState(null);
  const [copied, setCopied] = useState({});
  const [relinking, setRelinking] = useState(false);
  const [relinkResult, setRelinkResult] = useState(null);
  const [menuStatus, setMenuStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [dlCreds, setDlCreds] = useState(false);
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    if (!token) return;
    let objUrl: string | null = null;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/credentials-pdf/thumbnail`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (res.ok) {
          const blob = await res.blob();
          objUrl = window.URL.createObjectURL(blob);
          setThumbUrl(objUrl);
        }
      } catch { /* ignore */ }
    })();
    return () => { if (objUrl) window.URL.revokeObjectURL(objUrl); };
  }, [token]);

  const openPreview = async () => {
    setPreviewOpen(true);
    if (pdfUrl) return;
    setLoadingPreview(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/credentials-pdf`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const blob = await res.blob();
        setPdfUrl(window.URL.createObjectURL(blob));
      }
    } catch { /* ignore */ } finally {
      setLoadingPreview(false);
    }
  };

  const handleDownloadCredentials = async () => {
    setDlCreds(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/credentials-pdf`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!res.ok) throw new Error('failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Angel_Massage_Site_Credentials.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download credentials PDF. Please try again.');
    } finally {
      setDlCreds(false);
    }
  };

  const fetchMenuStatus = async () => {
    setLoadingStatus(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/rich-menu-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMenuStatus(data);
      }
    } catch (err) {
      console.error('Failed to fetch menu status:', err);
    } finally {
      setLoadingStatus(false);
    }
  };

  useEffect(() => {
    if (token) fetchMenuStatus();
  }, [token]);

  const handleSetupRichMenu = async () => {
    setSettingUpMenu(true);
    setMenuResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/setup-rich-menu`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setMenuResult({ success: true, message: data.message, id: data.rich_menu_id });
        fetchMenuStatus();
      } else {
        setMenuResult({ success: false, message: data.detail || 'Failed to setup Rich Menu' });
      }
    } catch (err) {
      setMenuResult({ success: false, message: 'Network error' });
    } finally {
      setSettingUpMenu(false);
    }
  };

  const handleRelinkRichMenus = async () => {
    setRelinking(true);
    setRelinkResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/relink-rich-menus`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setRelinkResult({ success: true, message: data.message });
        fetchMenuStatus();
      } else {
        setRelinkResult({ success: false, message: data.detail || 'Failed to relink Rich Menus' });
      }
    } catch (err) {
      setRelinkResult({ success: false, message: 'Network error' });
    } finally {
      setRelinking(false);
    }
  };

  return (
  <div className="space-y-6">
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Menu className="w-5 h-5 text-emerald-600" />
          {t('admin.lineRichMenu') || 'LINE Rich Menu'}
        </CardTitle>
        <CardDescription>
          {t('admin.lineRichMenuDesc') || 'Setup or update the LINE Rich Menu for all users. Includes buttons for booking, viewing therapists, rates, website, contact, and staff login.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="p-5 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border border-emerald-200">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-6 h-6 text-emerald-700" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-emerald-800 mb-1">Rich Menu Buttons</h3>
              <div className="grid grid-cols-3 gap-2 mb-4">
                {['Book Now', 'Our Therapists', 'Rates & Services', 'Website', 'Contact Us', 'Staff Login'].map((btn) => (
                  <span key={btn} className="text-xs bg-white px-2 py-1 rounded border border-emerald-200 text-emerald-700 text-center">{btn}</span>
                ))}
              </div>
              <p className="text-sm text-emerald-600 mb-3">
                Tapping "Our Therapists" will show a photo carousel of all therapists with names, descriptions, and a Book Now button.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button onClick={handleSetupRichMenu} disabled={settingUpMenu} data-testid="setup-rich-menu-btn"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  {settingUpMenu ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Setting up...</> : 'Setup / Update Rich Menu'}
                </Button>
                <Button onClick={handleRelinkRichMenus} disabled={relinking} data-testid="relink-rich-menus-btn"
                  variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                  {relinking ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Relinking...</> : <><Link2 className="w-4 h-4 mr-2" /> Relink to All Followers</>}
                </Button>
                <Button onClick={fetchMenuStatus} disabled={loadingStatus} variant="ghost" size="sm" className="text-emerald-600">
                  <RefreshCw className={`w-4 h-4 ${loadingStatus ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              {menuResult && (
                <p className={`mt-2 text-sm ${menuResult.success ? 'text-emerald-700' : 'text-red-600'}`} data-testid="rich-menu-result">
                  {menuResult.message} {menuResult.id && <span className="text-xs text-gray-500">({menuResult.id})</span>}
                </p>
              )}
              {relinkResult && (
                <p className={`mt-2 text-sm ${relinkResult.success ? 'text-emerald-700' : 'text-red-600'}`} data-testid="relink-result">
                  {relinkResult.message}
                </p>
              )}
              {menuStatus && (
                <div className="mt-4 p-3 bg-white rounded-lg border border-emerald-100">
                  <h4 className="text-xs font-medium text-emerald-800 mb-2 uppercase tracking-wide">Rich Menu Status</h4>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-gray-500">EN Menu:</span>{' '}
                      {menuStatus.rich_menus?.en ? (
                        <span className="text-emerald-600 font-mono">{menuStatus.rich_menus.en.substring(0, 20)}...</span>
                      ) : (
                        <span className="text-red-500">Not set</span>
                      )}
                    </div>
                    <div>
                      <span className="text-gray-500">TH Menu:</span>{' '}
                      {menuStatus.rich_menus?.th ? (
                        <span className="text-emerald-600 font-mono">{menuStatus.rich_menus.th.substring(0, 20)}...</span>
                      ) : (
                        <span className="text-red-500">Not set</span>
                      )}
                    </div>
                    <div>
                      <span className="text-gray-500">Total Followers:</span>{' '}
                      <span className="font-semibold text-emerald-700">{menuStatus.total_followers || 0}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Last Activity:</span>{' '}
                      <span className="text-gray-700">
                        {menuStatus.recent_followers?.[0]?.last_seen
                          ? new Date(menuStatus.recent_followers[0].last_seen).toLocaleDateString()
                          : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>

    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <FileText className="w-5 h-5 text-pink-600" />
          {t('admin.operatingManuals') || 'Operating Manuals'}
        </CardTitle>
        <CardDescription>
          {t('admin.manualsDesc') || 'Download PDF operating manuals for administrators and therapists in English and Thai.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-5 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-1 flex items-center gap-2">
              <Shield className="w-4 h-4" />
              {t('admin.adminManual') || 'Admin Manual'}
            </h3>
            <p className="text-sm text-blue-600 mb-4">
              {t('admin.adminManualDesc') || 'Complete guide for managing bookings, therapists, revenue, and system settings.'}
            </p>
            <div className="flex flex-col gap-2">
              <a href={`${API_URL}/api/manuals/Angel_Massage_Admin_Manual_EN.pdf`} target="_blank" rel="noopener noreferrer" download data-testid="download-admin-manual-en">
                <Button variant="outline" className="w-full justify-start bg-white hover:bg-blue-50">
                  <Download className="w-4 h-4 mr-2" /> Admin Manual (English)
                </Button>
              </a>
              <a href={`${API_URL}/api/manuals/Angel_Massage_Admin_Manual_TH.pdf`} target="_blank" rel="noopener noreferrer" download data-testid="download-admin-manual-th">
                <Button variant="outline" className="w-full justify-start bg-white hover:bg-blue-50">
                  <Download className="w-4 h-4 mr-2" /> {t('admin.adminManualTH') || 'Admin Manual (Thai / ภาษาไทย)'}
                </Button>
              </a>
            </div>
          </div>

          <div className="p-5 bg-gradient-to-br from-pink-50 to-pink-100 rounded-xl border border-pink-200">
            <h3 className="font-semibold text-pink-800 mb-1 flex items-center gap-2">
              <Users className="w-4 h-4" />
              {t('admin.therapistManual') || 'Therapist Manual'}
            </h3>
            <p className="text-sm text-pink-600 mb-4">
              {t('admin.therapistManualDesc') || 'Guide for therapists on managing bookings, LINE integration, and service guidelines.'}
            </p>
            <div className="flex flex-col gap-2">
              <a href={`${API_URL}/api/manuals/Angel_Massage_Therapist_Manual_EN.pdf`} target="_blank" rel="noopener noreferrer" download data-testid="download-therapist-manual-en">
                <Button variant="outline" className="w-full justify-start bg-white hover:bg-pink-50">
                  <Download className="w-4 h-4 mr-2" /> Therapist Manual (English)
                </Button>
              </a>
              <a href={`${API_URL}/api/manuals/Angel_Massage_Therapist_Manual_TH.pdf`} target="_blank" rel="noopener noreferrer" download data-testid="download-therapist-manual-th">
                <Button variant="outline" className="w-full justify-start bg-white hover:bg-pink-50">
                  <Download className="w-4 h-4 mr-2" /> {t('admin.therapistManualTH') || 'Therapist Manual (Thai / ภาษาไทย)'}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>

    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Shield className="w-5 h-5 text-amber-600" />
          {t('admin.siteCredentials') || 'Site Access Credentials'}
        </CardTitle>
        <CardDescription>
          {t('admin.siteCredentialsDesc') || 'Confidential PDF with all admin usernames and passwords, plus how therapists log in. Admin-only — not stored on any public page.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="p-5 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200">
          <p className="text-sm text-amber-700 mb-4">
            {t('admin.siteCredentialsWarning') || 'This file contains login passwords. Keep it private and store it in a password manager.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-5 items-start">
            {/* Preview thumbnail */}
            <button
              type="button"
              onClick={openPreview}
              data-testid="credentials-thumbnail-btn"
              className="group relative w-40 shrink-0 rounded-lg overflow-hidden border border-amber-300 bg-white shadow-sm hover:shadow-md transition-all"
              title={t('admin.previewCredentials') || 'Click to preview'}
            >
              {thumbUrl ? (
                <img src={thumbUrl} alt="Credentials PDF preview" className="w-full h-auto block" data-testid="credentials-thumbnail-img" />
              ) : (
                <div className="w-40 h-56 flex items-center justify-center bg-amber-50">
                  <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
                </div>
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                <span className="opacity-0 group-hover:opacity-100 transition-opacity text-white text-sm font-medium flex items-center gap-1">
                  <Eye className="w-4 h-4" /> {t('admin.preview') || 'Preview'}
                </span>
              </div>
            </button>

            <div className="flex-1 space-y-2">
              <p className="text-sm text-amber-800">
                {t('admin.previewHint') || 'Preview the document to confirm it looks right before you download or share it.'}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button onClick={openPreview} data-testid="preview-credentials-btn"
                  variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-100">
                  <Eye className="w-4 h-4 mr-2" /> {t('admin.previewCredentials') || 'Preview'}
                </Button>
                <Button onClick={handleDownloadCredentials} disabled={dlCreds} data-testid="download-credentials-btn"
                  className="bg-amber-600 hover:bg-amber-700 text-white">
                  {dlCreds ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> {t('common.loading') || 'Preparing...'}</> : <><Download className="w-4 h-4 mr-2" /> {t('admin.downloadCredentials') || 'Download Credentials PDF'}</>}
                </Button>
              </div>
            </div>
          </div>
        </div>

        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="max-w-3xl w-[95vw] h-[85vh] flex flex-col" data-testid="credentials-preview-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-amber-600" />
                {t('admin.siteCredentials') || 'Site Access Credentials'}
              </DialogTitle>
              <DialogDescription>
                {t('admin.previewHint') || 'Preview the document to confirm it looks right before you download or share it.'}
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 min-h-0 rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
              {loadingPreview || !pdfUrl ? (
                <div className="w-full h-full flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
                </div>
              ) : (
                <iframe src={pdfUrl} title="Credentials PDF" className="w-full h-full" data-testid="credentials-preview-iframe" />
              )}
            </div>
            <div className="flex justify-end">
              <Button onClick={handleDownloadCredentials} disabled={dlCreds} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="preview-download-btn">
                {dlCreds ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> {t('common.loading') || 'Preparing...'}</> : <><Download className="w-4 h-4 mr-2" /> {t('admin.downloadCredentials') || 'Download Credentials PDF'}</>}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>

    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Send className="w-5 h-5 text-green-600" />
          {t('admin.sendManualLine') || 'Send Manual to Therapist'}
        </CardTitle>
        <CardDescription>
          {t('admin.sendManualLineDesc') || 'Send or share the therapist manual with your team via LINE or copy the download link.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {therapists.map((th) => {
            const copyLink = (lang) => {
              const filename = lang === 'en' ? 'Angel_Massage_Therapist_Manual_EN.pdf' : 'Angel_Massage_Therapist_Manual_TH.pdf';
              const url = `${API_URL}/api/manuals/${filename}`;
              navigator.clipboard.writeText(url);
              const key = `${th.id}_${lang}`;
              setCopied(prev => ({ ...prev, [key]: true }));
              setTimeout(() => setCopied(prev => ({ ...prev, [key]: false })), 2000);
            };
            return (
              <div key={th.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-testid={`send-manual-row-${th.id}`}>
                <div className="flex items-center gap-3">
                  <img src={th.photo_url} alt={th.name} className="w-10 h-10 rounded-full object-cover" />
                  <div>
                    <p className="font-medium text-sm">{th.name}</p>
                    <p className="text-xs text-gray-500">{th.phone_number || 'No phone'}</p>
                    {th.line_user_id ? (
                      <span className="text-xs text-green-600">LINE linked</span>
                    ) : (
                      <span className="text-xs text-orange-500">LINE not linked</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap justify-end">
                  {th.line_user_id ? (
                    <>
                      <Button size="sm" variant="outline" disabled={sendingManual[`${th.id}_therapist_en`]}
                        onClick={() => handleSendManualViaLine(th.id, 'therapist_en', th.name)} data-testid={`send-manual-en-${th.id}`} className="text-xs">
                        {sendingManual[`${th.id}_therapist_en`] ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />} EN
                      </Button>
                      <Button size="sm" variant="outline" disabled={sendingManual[`${th.id}_therapist_th`]}
                        onClick={() => handleSendManualViaLine(th.id, 'therapist_th', th.name)} data-testid={`send-manual-th-${th.id}`} className="text-xs">
                        {sendingManual[`${th.id}_therapist_th`] ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />} TH
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button size="sm" variant="outline" onClick={() => copyLink('en')} data-testid={`copy-manual-en-${th.id}`} className="text-xs">
                        {copied[`${th.id}_en`] ? <Check className="w-3 h-3 mr-1 text-green-600" /> : <Copy className="w-3 h-3 mr-1" />}
                        {copied[`${th.id}_en`] ? 'Copied!' : 'Copy EN Link'}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => copyLink('th')} data-testid={`copy-manual-th-${th.id}`} className="text-xs">
                        {copied[`${th.id}_th`] ? <Check className="w-3 h-3 mr-1 text-green-600" /> : <Copy className="w-3 h-3 mr-1" />}
                        {copied[`${th.id}_th`] ? 'Copied!' : 'Copy TH Link'}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  </div>
  );
};
